/**
 * useUserGroups
 * The instance's user groups (`users/groups`, read-only) and the action that
 * sets one internal user's membership through the `updateUserGroups` callable.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchUserGroups, updateUserGroups, UserGroup } from '../api/userGroupsApi';

interface UseUserGroupsReturn {
    groups: UserGroup[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    setUserGroups: (uid: string, groups: string[]) => Promise<void>;
    clearError: () => void;
}

const errorMessage = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

export const useUserGroups = (): UseUserGroupsReturn => {
    const [groups, setGroups] = useState<UserGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchUserGroups()
            .then(result => { if (!cancelled) setGroups(result); })
            .catch(err => { if (!cancelled) setError(errorMessage(err, 'Kunde inte läsa användargrupper')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const setUserGroups = useCallback(async (uid: string, next: string[]) => {
        setSubmitting(true);
        setError(null);
        try {
            await updateUserGroups(uid, next);
        } catch (err) {
            setError(errorMessage(err, 'Kunde inte uppdatera grupptillhörighet'));
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, []);

    return { groups, loading, error, submitting, setUserGroups, clearError: () => setError(null) };
};
